import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Trash2 } from 'lucide-react';
import QRCodeStyling from 'qr-code-styling';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { UserTemplate } from '@/hooks/useTemplates';
import { format } from 'date-fns';

interface UserTemplateCardProps {
  template: UserTemplate;
  onUse: (template: UserTemplate) => void;
  onDelete: () => void;
}

export function UserTemplateCard({ template, onUse, onDelete }: UserTemplateCardProps) {
  const qrRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const settings = template.design_settings;
    
    const qrCode = new QRCodeStyling({
      width: 100,
      height: 100,
      data: 'https://example.com',
      dotsOptions: {
        color: settings.dotsColor || '#1a1a2e',
        type: settings.dotsType || 'rounded',
      },
      backgroundOptions: {
        color: settings.backgroundColor || '#ffffff',
      },
      cornersSquareOptions: {
        type: settings.cornersSquareType || 'extra-rounded',
        color: settings.cornersSquareColor || settings.dotsColor || '#1a1a2e',
      },
      cornersDotOptions: {
        type: settings.cornersDotType || 'dot',
        color: settings.cornersDotColor || settings.dotsColor || '#1a1a2e',
      },
    });

    if (qrRef.current) {
      qrRef.current.innerHTML = '';
      qrCode.append(qrRef.current);
    }
  }, [template]);

  return (
    <motion.div whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
      <Card className="overflow-hidden group hover:shadow-md transition-all">
        <CardContent className="p-4">
          {/* QR Preview */}
          <div className="relative flex items-center justify-center mb-3 p-3 rounded-lg bg-muted/30">
            <div ref={qrRef} className="rounded-lg overflow-hidden" />
            
            {/* Delete Button */}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-1 right-1 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Template</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete "{template.name}"? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {/* Template Info */}
          <div className="space-y-1.5">
            <h3 className="font-medium text-sm line-clamp-1">{template.name}</h3>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">
                {template.qr_type}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {format(new Date(template.created_at), 'MMM d, yyyy')}
              </span>
            </div>
          </div>

          {/* Action Button */}
          <Button
            onClick={() => onUse(template)}
            className="w-full mt-3"
            size="sm"
          >
            Use Template
          </Button>
        </CardContent>
      </Card>
    </motion.div>
  );
}
